import * as React from 'react';
import { useLocale, useTranslations } from '@accenturecodefoundry/mfe-pot-shared-i18n';
import { assetBaseUrl } from '../asset-base-url';
import './BenefitCard.css';

export interface BenefitCardProps {
  iconName: string;
  /** Controls icon-well background and icon colour. 'brand' = blue tint; 'neutral' = gray. */
  iconVariant: 'brand' | 'neutral';
  /** i18n key for the badge pill text (amount / qualifier). Omit for no badge. */
  badgeKey?: string;
  /** Direct override for the badge text — used for dynamic values (e.g. province name). When
   *  provided, renders instead of badgeKey. When both are undefined, no badge is rendered. */
  badgeText?: string;
  titleKey: string;
  noteKey: string;
  href: string;
  ctaLabelKey: string;
}

/**
 * A single benefit card: icon well + optional badge pill + heading + body note + CTA link.
 * Flat border-only card (no shadow) per the `src` variant of OverviewScreen.tsx in design1.
 * Self-serves locale/translations, same convention as every other kit component.
 */
export function BenefitCard({ iconName, iconVariant, badgeKey, badgeText, titleKey, noteKey, href, ctaLabelKey }: BenefitCardProps) {
  const locale = useLocale();
  const { t } = useTranslations(assetBaseUrl, locale);

  const resolvedBadge = badgeText ?? (badgeKey ? t(badgeKey) : undefined);

  return (
    <article className="benefit-card">
      <div className="benefit-card__header">
        <div
          aria-hidden="true"
          className={`benefit-card__icon-well benefit-card__icon-well--${iconVariant}`}
        >
          <scds-icon name={iconName} size="md" />
        </div>
        {resolvedBadge && (
          <span className="benefit-card__badge">
            {resolvedBadge}
          </span>
        )}
      </div>
      <h3 className="benefit-card__title">{t(titleKey)}</h3>
      <p className="benefit-card__note">{t(noteKey)}</p>
      <a href={href} className="benefit-card__cta">
        {t(ctaLabelKey)}
        <scds-icon name="chevron-right" size="sm" aria-hidden="true" />
      </a>
    </article>
  );
}
